/**
 * deviceType — SWEEP-02 / F-SW7-11
 *
 * Normalisiert BE-device_type-Werte für die Anzeige.
 * BE-DB-Default ist "unkown" (Tippfehler), daher beide Varianten abfangen.
 *
 * F-SW7-11: Reine Sentinel-Werte ("unkown"/"unknown"/"not_found" — z.B. liefert
 * /api/v2/hotspots device_type: "not_found" für Hotspots ohne Asset-Lookup)
 * werden über den geteilten `hotspotStatusLabel`-Helper (Owner dieser Story,
 * src/lib/hotspotStatusLabel.ts) i18n-übersetzt, statt roh/hartcodiert-Englisch
 * ("Not_found"/"Unknown") gerendert zu werden.
 */
import i18next from '../../i18n'
import { hotspotStatusLabel, isRawStatusSentinel, type TranslateFn } from '../../lib/hotspotStatusLabel'

// Labels entsprechen den bisherigen Anzeige-Labels der Hotspots-Liste.
// unkown/unknown werden über i18next.t (globale Instanz) übersetzt, siehe
// deviceTypeLabel unten — der Fachbegriff-Rest bleibt hartcodiert (A1).
const DEVICE_TYPE_MAP: Record<string, string> = {
  iot: 'IoT · LoRaWAN',
  cbrs: 'CBRS · Mobile',
  wifi: 'Wi-Fi',
  wifioutdoor: 'Wi-Fi · Outdoor',
  wifiindoor: 'Wi-Fi · Indoor',
}

/**
 * Gibt ein lesbares Label für den device_type zurück.
 *
 * @param dt Roh-Wert vom Backend
 * @param t  Optionale i18next-Übersetzungsfunktion. Wenn übergeben, werden
 *           bekannte Sentinel-Werte ("unkown"/"unknown"/"not_found") über den
 *           geteilten Helper i18n-übersetzt (Vorrang). Ohne `t` (Legacy-Aufrufer)
 *           übersetzt "unkown"/"unknown" weiterhin i18n-geführt via globaler
 *           i18next-Instanz (`hotspots:deviceType.unknown`); nur echte,
 *           unbekannte Werte greifen auf den hartcodierten Capitalize-Fallback zu.
 */
export function deviceTypeLabel(dt: string | null | undefined, t?: TranslateFn): string {
  if (!dt) return ''
  if (isRawStatusSentinel(dt) && t) return hotspotStatusLabel(dt, t)
  const key = dt.toLowerCase()
  if (key === 'unkown' || key === 'unknown') return i18next.t('hotspots:deviceType.unknown')
  if (key in DEVICE_TYPE_MAP) return DEVICE_TYPE_MAP[key]
  // Capitalize-Fallback (kein t übergeben, oder Wert ist kein bekannter Sentinel)
  return dt.charAt(0).toUpperCase() + dt.slice(1)
}
